class ClassesGrid {
  constructor() {
    this.gridEl = document.getElementById('classes-grid');
    if (!this.gridEl) return;

    this.config = this.initializeConfig();
    this.modal = new VideoModal();

    // Search / filter UI refs
    this.searchInput       = document.getElementById('classes-search-input');
    this.filterToggle      = document.getElementById('classes-filter-toggle');
    this.filterPanel       = document.getElementById('classes-filter-panel');
    this.filterGuide       = document.getElementById('classes-filter-guide');
    this.filterCategory    = document.getElementById('classes-filter-category');
    this.filterCategoryRow = document.getElementById('classes-category-row');
    this.filterTag         = document.getElementById('classes-filter-tag');
    this.filterTagRow      = document.getElementById('classes-tag-row');
    this.filterClear       = document.getElementById('classes-filter-clear');
    this.filterBadge       = this.filterToggle?.querySelector('.classes-searchbar__badge');
    this._searchDebounce   = null;
    this._filtersPopulated = false;

    // Restore filter state from URL on page load
    const _up = new URLSearchParams(location.search);
    if (this.searchInput) this.searchInput.value = _up.get('q') || '';
    this._pendingGuide    = _up.get('guide') || '';
    // Normalise to slug format so both legacy (?category=sound healing) and
    // new (?category=sound-healing) links map correctly to the server-side filter.
    this._pendingCategory = (_up.get('category') || '').toLowerCase().replace(/\s+/g, '-');
    this._pendingTag      = _up.get('tag') || '';

    this.init();
  }

  initializeConfig() {
    const urlParams = new URLSearchParams(location.search);

    return {
      // API base URL is passed via data-api attribute (set from Liquid metafield in the section)
      apiBase:           this.gridEl.dataset.api || '',
      lockedRedirectUrl: this.gridEl.dataset.lockedUrl || '/pages/membership',
      categoryFilter:    this.parseFilter(this.gridEl.dataset.category || urlParams.get('category') || ''),
      teacherFilter:     this.parseFilter(this.gridEl.dataset.teacher  || urlParams.get('teacher')  || urlParams.get('guide') || ''),
      placeholderImg:    this.createPlaceholderImage(),
    };
  }

  parseFilter(filterString) {
    if (!filterString) return [];
    return String(filterString)
      .split(/[|,]/)
      .map(item => item.trim())
      .filter(Boolean)
      .map(this.normalizeString);
  }

  normalizeString(str = '') {
    return str.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '').trim();
  }

  createPlaceholderImage() {
    return 'https://cdn.shopify.com/s/files/1/0708/7482/4875/files/endota-logo-padded.jpg?v=1768792411';
  }

  buildRequestUrl() {
    const url = new URL(this.config.apiBase, location.origin);

    if (this.config.categoryFilter.length) {
      url.searchParams.set('category', this.config.categoryFilter.join(','));
    }
    if (this.config.teacherFilter.length) {
      url.searchParams.set('teacher', this.config.teacherFilter.join(','));
    }

    // Search / filter bar params (Storyblok path — server-side filtered)
    const keyword = this.searchInput?.value.trim();
    if (keyword) url.searchParams.set('keyword', keyword);

    const guide = this.filterGuide?.value || this._pendingGuide;
    if (guide) url.searchParams.set('teacherIds', guide);

    const category = this.filterCategory?.value || this._pendingCategory;
    if (category) url.searchParams.set('categories', category);

    const tag = this.filterTag?.value || this._pendingTag;
    if (tag) url.searchParams.set('tags', tag);

    // Set in browser console: localStorage.setItem('retreat_origin', 'storyblok')
    // Clear with:            localStorage.removeItem('retreat_origin')
    const origin = localStorage.getItem('retreat_origin');
    if (origin) url.searchParams.set('origin', origin);

    return url.toString();
  }

  getNestedProperty(obj, path, fallback = null) {
    try {
      return path.split('.').reduce((current, key) => current?.[key], obj) ?? fallback;
    } catch {
      return fallback;
    }
  }

  extractVimeoId(url) {
    if (!url) return null;
    const match = String(url).match(/(?:vimeo\.com\/(?:video\/)?)(\d+)/);
    return match ? match[1] : null;
  }

  getLessonThumbnail(lesson) {
    const thumbnailPaths = [
      'mediaResponse.thumbnailDto.cdnMedium',
      'mediaResponse.thumbnailDto.cdnLarge',
      'mediaResponse.thumbnailDto.cdnSmall',
    ];

    for (const path of thumbnailPaths) {
      const thumbnail = this.getNestedProperty(lesson, path);
      if (thumbnail?.startsWith('https://endotaspa.com.au')) continue;
      if (thumbnail) return thumbnail;
    }

    return 'https://cdn.shopify.com/s/files/1/0708/7482/4875/files/endota-logo-padded.jpg?v=1768792411';
  }

  createVideoEmbed(url) {
    const vimeoId = this.extractVimeoId(url);
    if (vimeoId) return this.createVimeoEmbed(vimeoId);
    if (/\.(mp4|webm|ogg)(\?|#|$)/i.test(url)) return this.createVideoElement(url);
    return this.createGenericIframe(url);
  }

  createVimeoEmbed(id) {
    const iframe = document.createElement('iframe');
    iframe.src = `https://player.vimeo.com/video/${id}?title=0&byline=0&portrait=0`;
    iframe.allow = 'autoplay; fullscreen; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.title = 'Course video';
    Object.assign(iframe.style, { width: '100%', height: '100%', border: '0' });
    return iframe;
  }

  createVideoElement(url) {
    const video = document.createElement('video');
    video.controls = true;
    video.playsInline = true;
    video.src = url;
    Object.assign(video.style, { width: '100%', height: '100%' });
    return video;
  }

  createGenericIframe(url) {
    const iframe = document.createElement('iframe');
    iframe.src = url;
    iframe.allowFullscreen = true;
    Object.assign(iframe.style, { width: '100%', height: '100%', border: '0' });
    return iframe;
  }

  buildLessonCard(lesson) {
    const { id, name, duration, lock, membershipType, tag } = lesson;
    const thumbnail = this.getLessonThumbnail(lesson);
    const mediaUrl  = this.getNestedProperty(lesson, 'mediaResponse.mediaDto.url');

    const card = document.createElement('article');
    card.className = (lock && membershipType === 2) ? 'class-card is-restricted' : 'class-card';
    card.setAttribute('data-id', id);

    const media = this.createCardMedia(lesson, thumbnail, mediaUrl);
    const body  = this.createCardBody(lesson);

    card.append(media, body);
    return card;
  }

  createCardMedia(lesson, thumbnail, mediaUrl) {
    const { id, name, lock, tag, membershipType } = lesson;

    const media = document.createElement('div');
    media.className = 'class-card__media';

    const img = document.createElement('img');
    img.loading = 'lazy';
    img.alt = name || 'Class thumbnail';
    img.src = thumbnail;
    media.appendChild(img);

    if (tag?.name) {
      const tagBadge = document.createElement('span');
      tagBadge.className = 'class-card__tag';
      tagBadge.textContent = tag.name;
      media.appendChild(tagBadge);
    }

    if (lock && membershipType) {
      media.appendChild(this.createMembershipPill(membershipType));
    }

    media.appendChild(this.createCardOverlay(lesson, mediaUrl));
    return media;
  }

  createCardOverlay(lesson, mediaUrl) {
    const { id, name, lock } = lesson;
    const overlay = document.createElement('div');

    if (!lock && mediaUrl) {
      overlay.className = 'class-card__overlay';
      const playButton = this.createPlayButton(lesson, mediaUrl);
      overlay.appendChild(playButton);

      overlay.parentNode?.addEventListener('click', (e) => {
        if (!e.target.closest('.class-card__play')) this.modal.open(mediaUrl);
      });
    } else if (lock) {
      overlay.className = 'class-card__restricted';
      overlay.appendChild(this.createLockedLink(id, name));
    }

    return overlay;
  }

  createPlayButton(lesson, mediaUrl) {
    const button = document.createElement('button');
    button.className = 'class-card__play';
    button.type = 'button';
    button.setAttribute('aria-label', 'Play video');
    button.addEventListener('click', () => {
      if (lesson.id) {
        const url = new URL(window.location.href);
        url.searchParams.set('vid', lesson.id);
        window.history.pushState({}, '', url.toString());
      }
      this.modal.open(mediaUrl);
    });
    return button;
  }

  createLockedLink(id, name) {
    const link = document.createElement('a');
    link.className = 'class-card__link';
    link.href = this.buildTrackingUrl(this.config.lockedRedirectUrl, {
      lessonId: id,
      lesson:   this.slugify(name),
    });
    link.textContent = name || 'View';
    return link;
  }

  createCardBody(lesson) {
    const { name, duration, lock, membershipType, introduction, categories, teacherResponse } = lesson;

    const body  = document.createElement('div');
    body.className = 'class-card__body';

    const title = document.createElement('div');
    title.className = 'class-card__title';
    title.textContent = name || 'Class';

    const meta = document.createElement('div');
    meta.className = 'class-card__meta';

    const intro = document.createElement('div');
    intro.className = 'class-card__intro';

    if (duration) {
      const durationSpan = document.createElement('span');
      durationSpan.textContent = duration;
      meta.appendChild(durationSpan);
    }

    if (introduction) {
      const introSpan = document.createElement('span');
      introSpan.innerHTML = introduction;
      intro.appendChild(introSpan);
    }

    const category = this.createCategorySection(categories, teacherResponse);
    body.append(title, meta, category, intro);
    return body;
  }

  createCategorySection(categories, teacherResponse) {
    const categoryDiv = document.createElement('div');
    categoryDiv.className = 'category';

    if (categories && categories.length > 0) {
      const categoriesSpan = document.createElement('span');
      categoriesSpan.className = 'cat';
      categoriesSpan.textContent = categories.map(cat => cat.name).join(', ');
      categoryDiv.appendChild(categoriesSpan);
    }

    if (teacherResponse?.name) {
      const teacherSpan = document.createElement('span');
      teacherSpan.className = 'cat teacher';
      teacherSpan.textContent = teacherResponse.name;
      categoryDiv.appendChild(teacherSpan);
    }

    return categoryDiv;
  }

  createMembershipPill(membershipType) {
    const pill = document.createElement('span');
    pill.className = 'class-card__pill';
    pill.textContent = { 1: 'guest pass', 2: 'complete member' }[membershipType] || 'members only';
    return pill;
  }

  createGroupSection(group, index) {
    const section = document.createElement('section');
    section.className = `class-group${index % 2 ? ' is-reversed' : ''}`;
    if (group.code) section.setAttribute('data-target-section-id', group.code);

    section.append(this.createGroupHero(group), this.createLessonsGrid(group.lessons || []));
    return section;
  }

  createGroupHero(group) {
    const hero = document.createElement('div');
    hero.className = 'class-group__hero';
    hero.append(this.createTextColumn(group), this.createMediaColumn(group));
    return hero;
  }

  createTextColumn(group) {
    const column = document.createElement('div');
    column.className = 'class-group__col class-group__col--text';

    const title = document.createElement('h2');
    title.className = 'class-group__title';
    title.textContent = group?.name || 'Classes';

    const description = document.createElement('div');
    description.className = 'class-group__desc';
    description.innerHTML = group?.description || '';

    column.append(title, description);
    return column;
  }

  createMediaColumn(group) {
    const column = document.createElement('div');
    column.className = 'class-group__col class-group__col--media';

    const videoWrapper = document.createElement('div');
    videoWrapper.className = 'class-group__video';

    const videoUrl = this.getNestedProperty(group, 'video.mediaDto.url');
    if (videoUrl) {
      videoWrapper.appendChild(this.createVideoEmbed(videoUrl));
    } else {
      // No video — fall back to the group header image if present
      const thumbUrl = this.getNestedProperty(group, 'video.thumbnailDto.cdnMedium')
        || this.getNestedProperty(group, 'video.thumbnailDto.cdnLarge')
        || this.getNestedProperty(group, 'video.thumbnailDto.cdnSmall');
      if (thumbUrl) {
        const img = document.createElement('img');
        img.src = thumbUrl;
        img.alt = group.name || '';
        img.className = 'class-group__header-image';
        videoWrapper.appendChild(img);
      }
    }

    column.appendChild(videoWrapper);
    return column;
  }

  createLessonsGrid(lessons) {
    const grid = document.createElement('div');
    grid.className = 'class-group__lessons';
    if (!lessons.length) return grid;

    const fragment = document.createDocumentFragment();
    lessons.forEach(lesson => fragment.appendChild(this.buildLessonCard(lesson)));
    grid.appendChild(fragment);
    return grid;
  }

  slugify(str = '') {
    return String(str).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  }

  buildTrackingUrl(baseUrl, params = {}) {
    const url = new URL(baseUrl, location.origin);
    Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));
    return url.toString();
  }

  async fetchClasses() {
    const requestUrl = this.buildRequestUrl();
    const customerAccessToken = window.customerAccessToken;

    const response = await fetch(requestUrl, {
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Customer-Access-Token': customerAccessToken,
      },
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    return response.json();
  }

  matchesFilters(lesson) {
    const lessonTeacher  = this.normalizeString(this.getNestedProperty(lesson, 'teacherResponse.name', ''));
    const teacherMatches = !this.config.teacherFilter.length || this.config.teacherFilter.includes(lessonTeacher);
    return teacherMatches;
  }

  renderClasses(payload) {
    const groups = this.getNestedProperty(payload, 'data.groups', []);
    this.gridEl.innerHTML = '';

    if (!groups.length) {
      this.renderEmptyState('No classes available.');
      return;
    }

    let hasRenderedContent = false;
    const fragment = document.createDocumentFragment();

    groups.forEach((group, index) => {
      const lessons         = Array.isArray(group.lessons) ? group.lessons : [];
      const filteredLessons = lessons.filter(lesson => this.matchesFilters(lesson));
      if (!filteredLessons.length) return;

      hasRenderedContent = true;
      fragment.appendChild(this.createGroupSection({ ...group, lessons: filteredLessons }, index));
    });

    if (hasRenderedContent) {
      this.gridEl.appendChild(fragment);
    } else {
      this.renderEmptyState('No classes match your filter.');
    }
  }

  renderEmptyState(message) {
    this.gridEl.innerHTML = `<p style="color:#444">${message}</p>`;
  }

  renderErrorState() {
    this.gridEl.innerHTML = '<p style="color:#c00">Sorry, something went wrong loading classes.</p>';
  }

  // ---------------------------------------------------------------------------
  // Search / filter bar
  // ---------------------------------------------------------------------------

  populateFilterOptions(groups) {
    const guides     = new Map();
    const categories = new Map();
    const tags       = new Map();

    groups.forEach(group => {
      (group.lessons || []).forEach(lesson => {
        const name = lesson.teacherResponse?.name;
        if (name) guides.set(name, name);

        (lesson.categories || []).forEach(cat => {
          if (cat.value && cat.name) categories.set(cat.value, cat.name);
        });

        const tag = lesson.tag;
        if (tag?.value && tag?.name) tags.set(tag.value, tag.name);
      });
    });

    [...guides.keys()].sort().forEach(name => {
      const opt = document.createElement('option');
      opt.value = name;
      opt.textContent = name;
      this.filterGuide?.appendChild(opt);
    });

    [...categories.entries()].sort((a, b) => a[1].localeCompare(b[1])).forEach(([value, name]) => {
      const opt = document.createElement('option');
      opt.value = value;
      opt.textContent = name;
      this.filterCategory?.appendChild(opt);
    });

    tags.forEach((displayName, value) => {
      const opt = document.createElement('option');
      opt.value = value;
      opt.textContent = displayName;
      this.filterTag?.appendChild(opt);
    });

    if (!categories.size && this.filterCategoryRow) this.filterCategoryRow.hidden = true;
    if (!tags.size      && this.filterTagRow)      this.filterTagRow.hidden      = true;

    if (this._pendingGuide    && this.filterGuide)    { this.filterGuide.value    = this._pendingGuide;    this._pendingGuide    = ''; }
    if (this._pendingCategory && this.filterCategory) { this.filterCategory.value = this._pendingCategory; this._pendingCategory = ''; }
    if (this._pendingTag      && this.filterTag)      { this.filterTag.value      = this._pendingTag;      this._pendingTag      = ''; }

    this.updateFilterState();
  }

  buildFilterOptionsUrl() {
    const url    = new URL(this.config.apiBase, location.origin);
    const origin = localStorage.getItem('retreat_origin');
    if (origin) url.searchParams.set('origin', origin);
    // Strip any filter params — we want the full unfiltered set for the dropdowns
    ['keyword', 'teacherIds', 'categories', 'tags'].forEach(p => url.searchParams.delete(p));
    return url.toString();
  }

  loadFilterOptions() {
    const customerAccessToken = window.customerAccessToken;
    fetch(this.buildFilterOptionsUrl(), {
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Customer-Access-Token': customerAccessToken,
      },
    })
      .then(r => r.json())
      .then(data => {
        const groups = this.getNestedProperty(data, 'data.groups', []);
        this.populateFilterOptions(groups);
      })
      .catch(() => { /* non-critical — dropdowns just won't be populated */ });
  }

  bindSearchEvents() {
    this.searchInput?.addEventListener('input', () => {
      clearTimeout(this._searchDebounce);
      this._searchDebounce = setTimeout(() => this.refetch(), 300);
    });

    this.filterGuide?.addEventListener('change',    () => this.refetch());
    this.filterCategory?.addEventListener('change', () => this.refetch());
    this.filterTag?.addEventListener('change',      () => this.refetch());
    this.filterClear?.addEventListener('click',     () => this.clearFilters());
    this.filterToggle?.addEventListener('click',    () => this.toggleFilterPanel());

    document.addEventListener('click', (e) => {
      const bar = document.getElementById('classes-searchbar');
      if (bar && !bar.contains(e.target)) this.closeFilterPanel();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeFilterPanel();
    });
  }

  refetch() {
    this.updateFilterState();
    this.syncUrl();
    this.init();
  }

  syncUrl() {
    const url      = new URL(location.href);
    const q        = this.searchInput?.value.trim();
    const guide    = this.filterGuide?.value    || this._pendingGuide;
    const tag      = this.filterTag?.value      || this._pendingTag;
    const category = this.filterCategory?.value || this._pendingCategory;
    if (q)        url.searchParams.set('q',        q);        else url.searchParams.delete('q');
    if (guide)    url.searchParams.set('guide',    guide);    else url.searchParams.delete('guide');
    if (category) url.searchParams.set('category', category); else url.searchParams.delete('category');
    if (tag)      url.searchParams.set('tag',      tag);      else url.searchParams.delete('tag');
    window.history.replaceState({}, '', url.toString());
  }

  clearFilters() {
    if (this.searchInput)    this.searchInput.value    = '';
    if (this.filterGuide)    this.filterGuide.value    = '';
    if (this.filterCategory) this.filterCategory.value = '';
    if (this.filterTag)      this.filterTag.value      = '';
    this.refetch();
  }

  toggleFilterPanel()  { this.filterPanel?.hidden ? this.openFilterPanel() : this.closeFilterPanel(); }
  openFilterPanel()    { if (this.filterPanel) this.filterPanel.hidden = false; this.filterToggle?.setAttribute('aria-expanded', 'true');  this.filterToggle?.classList.add('is-active'); }
  closeFilterPanel()   { if (this.filterPanel) this.filterPanel.hidden = true;  this.filterToggle?.setAttribute('aria-expanded', 'false'); this.filterToggle?.classList.remove('is-active'); }

  updateFilterState() {
    const count = [this.filterGuide?.value, this.filterCategory?.value, this.filterTag?.value].filter(Boolean).length;
    if (this.filterBadge) { this.filterBadge.textContent = count; this.filterBadge.hidden = count === 0; }
    if (this.filterClear)   this.filterClear.hidden = count === 0;
  }

  checkAndOpenVideoFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const videoId   = urlParams.get('vid');
    if (!videoId) return;

    const checkForCard = () => {
      const card = document.querySelector(`[data-id="${videoId}"]`);
      if (card) {
        const playButton = card.querySelector('.class-card__play');
        const overlay    = card.querySelector('.class-card__overlay');
        if (playButton) { playButton.click(); return true; }
        if (overlay)    { overlay.click();    return true; }
      }
      return false;
    };

    if (checkForCard()) return;

    let attempts = 0;
    const intervalId = setInterval(() => {
      attempts++;
      if (checkForCard() || attempts >= 20) clearInterval(intervalId);
    }, 100);
  }

  async init() {
    this.gridEl.classList.add('is-loading');
    try {
      const payload = await this.fetchClasses();
      this.renderClasses(payload);

      if (!this._filtersPopulated) {
        this._filtersPopulated = true;
        this.bindSearchEvents();
        this.loadFilterOptions();
      }

      this.checkAndOpenVideoFromUrl();
    } catch (error) {
      this.renderErrorState();
    } finally {
      this.gridEl.classList.remove('is-loading');
    }
  }
}

class VideoModal {
  constructor() {
    this.modal       = document.getElementById('video-modal');
    this.frame       = document.getElementById('video-frame');
    this.closeButton = this.modal?.querySelector('.video-modal__close');

    if (this.modal && this.frame && this.closeButton) this.bindEvents();
  }

  bindEvents() {
    this.closeButton.addEventListener('click', () => this.close());
    this.modal.addEventListener('click', (e) => { if (e.target === this.modal) this.close(); });
    document.addEventListener('keydown', (e) => { if (!this.modal.hidden && e.key === 'Escape') this.close(); });
  }

  open(videoUrl) {
    const vimeoId = this.extractVimeoId(videoUrl);
    const src     = vimeoId
      ? `https://player.vimeo.com/video/${vimeoId}?autoplay=1&title=0&byline=0&portrait=0`
      : videoUrl;
    if (!src) return;

    this.frame.src = src;
    this.modal.hidden = false;
    this.modal.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    this.closeButton.focus();
  }

  close() {
    this.frame.src = '';
    this.modal.hidden = true;
    this.modal.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';

    const url = new URL(window.location.href);
    url.searchParams.delete('vid');
    window.history.pushState({}, '', url.toString());
  }

  extractVimeoId(url) {
    if (!url) return null;
    const match = String(url).match(/(?:vimeo\.com\/(?:video\/)?)(\d+)/);
    return match ? match[1] : null;
  }
}

class SectionScroller {
  constructor(buttonSelector, headerSelector) {
    this.buttonSelector = buttonSelector;
    this.headerSelector = headerSelector;
    this.init();
  }

  init() {
    document.querySelectorAll(this.buttonSelector).forEach(btn => {
      btn.addEventListener('click', (event) => this.handleClick(event));
    });
  }

  handleClick(event) {
    const sectionId = event.currentTarget.dataset.targetId;
    const section   = document.querySelector(`[data-target-section-id="${sectionId}"]`);
    if (section) this.scrollToSection(section);
  }

  scrollToSection(section) {
    const header = document.querySelector(this.headerSelector);
    if (header) {
      window.scrollTo({ top: section.offsetTop - header.offsetHeight, behavior: 'smooth' });
    } else {
      section.scrollIntoView({ behavior: 'smooth' });
    }
    const sectionId = section.getAttribute('data-target-section-id');
    if (sectionId) history.pushState(null, '', `#${sectionId}`);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new ClassesGrid();
  new SectionScroller('[data-target-id]', 'header');
});
